import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, TrendingDown, Clock } from 'lucide-react';

const problems = [
  {
    icon: XCircle,
    title: 'Random Posting',
    description: "You post whenever you feel like it. No plan. No strategy. No results. Your feed looks like a guessing game.",
  },
  {
    icon: TrendingDown,
    title: 'Zero Conversions',
    description: "You get a few likes, maybe a comment. But nobody walks into your restaurant or buys from your store.",
  },
  {
    icon: Clock,
    title: 'Wasted Time & Money',
    description: "Hours spent creating content that nobody sees. Or paying someone who just posts pretty pictures with no strategy.",
  },
];

export const ProblemSection = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <span className="font-accent text-slate-400 mb-4 block" data-testid="problem-label">
            The Problem
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-900 leading-tight max-w-2xl mb-6"
            data-testid="problem-headline"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Why Most Local Businesses{' '}
            <span className="font-medium">Fail on Instagram</span>
          </h2>
          <p className="text-base md:text-lg text-slate-500 max-w-xl mb-16">
            If this sounds familiar, you're not alone. Most businesses make
            the same mistakes on Instagram.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {problems.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`problem-item-${i}`}
            >
              <div className="mb-4">
                <item.icon size={24} className="text-slate-400" strokeWidth={1.5} />
              </div>
              <h3
                className="text-lg font-medium text-slate-900 mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {item.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Visual banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 rounded-3xl overflow-hidden relative h-64 md:h-80"
          data-testid="problem-image"
        >
          <img
            src="https://images.unsplash.com/photo-1630659879694-b1750389d796?w=1200&h=400&fit=crop"
            alt="Food photography for social media"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/40 to-transparent flex items-center">
            <div className="px-8 md:px-12 max-w-lg">
              <p className="text-white text-xl md:text-2xl font-light leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your competitors are already winning on Instagram.{' '}
                <span className="font-medium">Don't get left behind.</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
